import { Card } from 'components/Container';
import { useDate } from 'components/hooks';
import { SectionInfo, SectionLabel } from 'components/Text';
import { IDateDifference } from 'configs/interfaces';
import { Moment } from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'rematch/store';

import TABLE_BUBBLE from '../../assets/table_bubble.png';
import TC from '../../assets/tablecaptain.png';

interface IBookingNotOpenedYet {
	now: Moment;
	start: Moment;
	slot: number;

}

export const BookingNotOpenedYet = (props: IBookingNotOpenedYet) => {
	const { now, start, slot } = props;
	const { getDateDifference, getEventDateFromFormat } = useDate();

	const [difference, setDifference] = useState<IDateDifference | undefined>(undefined);

	const decryptedToken = useSelector((store: IRootState) => store.auth.decryptedToken);
	const config = useSelector((store: IRootState) => store.data.config);

	// The day booking opens comes from Firestore config.bookingStart - the same field the countdown
	// is based on - so the text can never drift away from the countdown. `start` (bookingStart plus
	// the slot offset) is the fallback if the config has not arrived yet.
	const bookingStartDate = config?.bookingStart ? getEventDateFromFormat(config.bookingStart) : start;

	useEffect(() => {
		const diff = getDateDifference(now, start);
		setDifference(diff);
	}, [now, start]);

	const _renderTime = (value?: number, label?: string) => {
		return (
			<div className="flex flex-col items-center text-bitsyellow ">
				<div className="text-3xl font-bold leading-none">{value}</div>
				<div className="text-lg leading-none">{label}</div>
			</div>
		);
	};

	const _renderHorizontalSeperator = () => {
		return <div className="w-full my-4 h-px bg-lightgray" />;
	};

	const _renderSeperator = () => {
		return <div className="text-bitsyellow text-3xl mx-1"></div>;
	};

	return (
		<Card hatColor="text-danger-500">
			<div className="max-w-[800px] mx-auto">
				<div className="t font-bold text-lg md:text-2xl text-center text-bitsyellow">
					Hi {decryptedToken?.firstName}
					{slot === 1 ? `, you are in booking slot ${slot}` : ''} <br />
					Your starting time is {start.locale('en').format('DD MMMM HH:mm')}<br />
					Booking will start in:
				</div>
				{_renderHorizontalSeperator()}
				<div className="flex items-center justify-center text-bitsyellow">
					{_renderTime(difference?.days, 'days')}
					{_renderSeperator()}
					{_renderTime(difference?.hours, 'hours')}
					{_renderSeperator()}
					{_renderTime(difference?.minutes, 'minutes')}
					{_renderSeperator()}
					{_renderTime(difference?.seconds, 'seconds')}
				</div>
				{_renderHorizontalSeperator()}
				<div className="w-full flex items-start">
					<div className="flex-1 pr-6 text-white">
						{/* <SectionLabel label="Please note:" />
						<SectionInfo>
							<p className="text-sm">
								You can find detailed information about the Table Captains and FAQs{' '}
								<a className="text-bitsyellow underline font-semibold" href="https://www.bitsandpretzels.com/oktoberfest">
									here
								</a>
							</p>
						</SectionInfo> */}

						<SectionInfo>
							<div className="text-base font-semibold mb-2">What You Need to Know</div>
							<ul className="list-disc list-inside pl-4 text-sm">
								<li>On Day 3, we all go to Oktoberfest (Festhalle Schottenhamel).</li>
								<li>You’ll sit at a table of 8 with a Table Captain or join a Tribe Hub.</li>
								<li>Two rounds: 45 min each, with table switching.</li>
								<li>First come, first served → tables fill up quickly.</li>
								<li>No stress: everyone will have a seat, even without booking.</li>
							</ul>
						</SectionInfo>



						<SectionLabel label="What is a Table Captain? / Tribe Hub?" />
						<SectionInfo>
							<div className="mb-2">
								<div className="font-semibold text-base my-2">Table Captain</div>
								<ul className="list-disc list-inside pl-4 text-sm">
									<li>Handpicked expert (founder, investor, CEO, journalist).</li>
									<li>Hosts a small table, shares experience, answers questions.</li>
									<li>Great for advice</li>
								</ul>
							</div>
							<div>
								<div className="font-semibold text-base">Tribe Hub</div>
								<ul className="list-disc list-inside pl-4 text-sm">
									<li>No host.</li>
									<li>Attendees gather around shared interests (themes, industries, roles).</li>
									<li>Great to meet attendees that are sharing your passion.</li>
								</ul>
							</div>
						</SectionInfo>

						<SectionLabel label="How to Prepare" />
						<SectionInfo>
							<ul className="list-disc list-inside pl-4 text-sm">
								<li>
									<a
										href="https://www.bitsandpretzels.com/oktoberfest"
										target="_blank"
										rel="noopener noreferrer"
										className="text-bitsyellow underline font-semibold"
									>
										Browse captains now
									</a>
								</li>
								<li>Shortlist several backups.</li>
								<li>Be ready to book fast when your slot opens.</li>
								<li>Bookmark your personal booking link.</li>
								<li>
									If you don’t get your first choice &rarr; plenty of Tribe Hubs &amp; free seating are available.
								</li>
							</ul>
						</SectionInfo>


						<SectionLabel label={`Booking Instructions (Live from ${bookingStartDate.locale('en').format('D MMMM')})`} />
						<SectionInfo>
							<ol className="list-decimal list-inside pl-4 text-sm">
								<li>Click <span className="font-semibold">“Join Table”</span>.</li>
								<li>Select your Table Captain, Tribe Hub, or Free Seating.</li>
								<li>Confirm your reservation.</li>
								<li>Repeat for second round.</li>
							</ol>
						</SectionInfo>








					</div>
				</div>
				<div className="w-full md:w-3/4 xl:w-1/2 mx-auto">
					<img src={TABLE_BUBBLE} className="w-full object-contain" alt="table" />
					<a href="https://www.bitsandpretzels.com/oktoberfest" target="_blank" rel=" noreferrer noopener">
						<div className="bg-success-500 py-2 xl:py-4 mt-6">
							<div className="text-center font-bold text-white md:text-xl xl:text-2xl">See all Table Captains</div>
						</div>
					</a>
				</div>
			</div>
		</Card>
	);
};
